import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@mondivago/supabase';
import { useSupplierProfile } from './useSupplierProfile';
import type { PromoCodeRow, PromoCodeApplied, PromoCodeFormData } from '@mondivago/types';

export function usePromoCodes() {
  const { profile } = useSupplierProfile();
  const supplierId = profile?.id ?? null;
  const [promoCodes, setPromoCodes] = useState<PromoCodeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPromoCodes = useCallback(async () => {
    if (!supplierId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('tour_promo_codes')
        .select('*')
        .eq('supplier_id', supplierId)
        .order('created_at', { ascending: false });
      if (dbError) throw dbError;
      setPromoCodes((data ?? []) as PromoCodeRow[]);
    } catch {
      setError('Error loading promo codes.');
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => {
    void fetchPromoCodes();
  }, [fetchPromoCodes]);

  const createPromoCode = useCallback(
    async (data: PromoCodeFormData) => {
      if (!supplierId) return { success: false, error: 'Not authenticated' };
      try {
        const { error: dbError } = await supabase.from('tour_promo_codes').insert({
          supplier_id: supplierId,
          code: data.code.toUpperCase(),
          tour_id: data.tourId,
          discount_type: data.discountType,
          discount_value: data.discountValue,
          max_uses: data.maxUses,
          is_active: true,
          expires_at: data.expiresAt || null,
        });
        if (dbError) throw dbError;
        void fetchPromoCodes();
        return { success: true };
      } catch {
        return { success: false, error: 'Error creating promo code.' };
      }
    },
    [supplierId, fetchPromoCodes]
  );

  const togglePromoCode = useCallback(async (id: string, isActive: boolean) => {
    try {
      const { error: dbError } = await supabase.from('tour_promo_codes').update({ is_active: isActive }).eq('id', id);
      if (dbError) throw dbError;
      setPromoCodes((prev) => prev.map((p) => (p.id === id ? { ...p, is_active: isActive } : p)));
      return { success: true };
    } catch {
      return { success: false, error: 'Error updating promo code.' };
    }
  }, []);

  const deletePromoCode = useCallback(async (id: string) => {
    try {
      const { error: dbError } = await supabase.from('tour_promo_codes').delete().eq('id', id);
      if (dbError) throw dbError;
      setPromoCodes((prev) => prev.filter((p) => p.id !== id));
      return { success: true };
    } catch {
      return { success: false, error: 'Error deleting promo code.' };
    }
  }, []);

  return { promoCodes, loading, error, refresh: fetchPromoCodes, createPromoCode, togglePromoCode, deletePromoCode };
}

export type { PromoCodeFormData } from '@mondivago/types';

export function useApplyPromoCode() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyPromoCode = useCallback(
    async (code: string, tourId: string, basePrice: number): Promise<PromoCodeApplied | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: dbError } = await supabase
          .from('tour_promo_codes')
          .select('*')
          .eq('code', code.toUpperCase())
          .eq('tour_id', tourId)
          .eq('is_active', true)
          .maybeSingle();
        if (dbError) throw dbError;
        if (!data) {
          setError('Invalid or expired promo code.');
          return null;
        }
        const promo = data as PromoCodeRow;
        if (promo.current_uses >= promo.max_uses) {
          setError('This promo code has reached its maximum usage limit.');
          return null;
        }
        if (promo.expires_at && new Date(promo.expires_at) < new Date()) {
          setError('This promo code has expired.');
          return null;
        }
        const discountAmount =
          promo.discount_type === 'percentage'
            ? Math.round(basePrice * (promo.discount_value / 100) * 100) / 100
            : Math.min(promo.discount_value, basePrice);
        return {
          id: promo.id,
          code: promo.code,
          discount_type: promo.discount_type as 'percentage' | 'fixed',
          discount_value: promo.discount_value,
          discount_amount: discountAmount,
          basePrice,
          finalPrice: Math.max(0, basePrice - discountAmount),
        };
      } catch {
        setError('Error validating promo code.');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clearPromoCode = useCallback(() => {
    setError(null);
  }, []);

  return { applyPromoCode, clearPromoCode, loading, error };
}
